import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, RefreshCw, User, Download, Share2, Tag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { resolveImg, formatDate } from "@/lib/util";
import { YouTubeEmbed } from "@/components/site/YouTubeEmbed";
import { PublicLayout } from "@/components/site/PublicLayout";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useSeo({
    title: post ? (post.seo_title || post.title) + " | Adv. Guruvayoorappan K V" : "Article",
    description: post?.seo_description || post?.excerpt,
    ogImage: resolveImg(post?.og_image || post?.featured_image),
    canonical: post?.canonical_url || undefined,
    robots: post?.robots || "index, follow",
    jsonLd: post ? {
      "@context": "https://schema.org", "@type": "Article", headline: post.title,
      image: resolveImg(post.featured_image), datePublished: post.published_at, dateModified: post.updated_at,
      author: { "@type": "Person", name: post.author }, articleSection: post.category,
    } : null,
  });

  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: post.title, url }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
  };

  if (notFound) return <PublicLayout><div className="max-w-3xl mx-auto px-6 py-24 text-center"><h1 className="font-serif text-3xl text-navy mb-4">Article not found</h1><Link to="/blog" className="text-brass">Back to Blog</Link></div></PublicLayout>;
  if (!post) return <PublicLayout><div className="max-w-3xl mx-auto px-6 py-32" /></PublicLayout>;

  return (
    <PublicLayout>
      <article>
        {/* Header */}
        <div className="bg-navy py-12 lg:py-16" data-testid="article-header">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <Link to="/blog" className="inline-flex items-center gap-2 text-ivory/50 hover:text-brass text-sm mb-6"><ArrowLeft size={15} /> Back to Blog</Link>
            <nav className="text-ivory/40 text-xs mb-4 font-mono" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-brass">Home</Link> / <Link to="/blog" className="hover:text-brass">Blog</Link> / <span className="text-brass">{post.category}</span>
            </nav>
            <h1 className="font-serif text-3xl lg:text-5xl text-ivory leading-tight mb-5">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-ivory/60 text-sm">
              <span className="flex items-center gap-1.5"><User size={14} className="text-brass" />{post.author}</span>
              <span className="flex items-center gap-1.5"><Calendar size={14} className="text-brass" />{formatDate(post.published_at)}</span>
              {post.updated_at && <span className="flex items-center gap-1.5"><RefreshCw size={13} className="text-brass" />Updated {formatDate(post.updated_at)}</span>}
              <span className="text-brass font-mono uppercase tracking-widest text-xs">{post.category}</span>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
          {post.featured_image && (
            <div className="rounded-lg overflow-hidden mb-10 border border-slate-200" style={{ aspectRatio: "3/2" }}>
              <img src={resolveImg(post.featured_image)} alt={post.featured_image_alt || post.title} className="w-full h-full object-cover" />
            </div>
          )}

          <div className="prose-legal" data-testid="article-content" dangerouslySetInnerHTML={{ __html: post.content }} />

          {/* YouTube */}
          {post.youtube_embeds?.map((u, i) => <YouTubeEmbed key={i} url={u} />)}
          {post.youtube_shorts?.length > 0 && (
            <div className="flex flex-wrap gap-4 justify-center my-6">
              {post.youtube_shorts.map((u, i) => <YouTubeEmbed key={i} url={u} short />)}
            </div>
          )}

          {/* PDFs */}
          {post.pdf_attachments?.map((pdf, i) => (
            <a key={i} href={resolveImg(pdf.url)} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-4 bg-ivory-dark border border-brass/30 rounded-lg p-5 my-6 hover:border-brass transition-colors" data-testid={`article-pdf-${i}`}>
              <span className="w-12 h-12 rounded-sm bg-navy text-brass flex items-center justify-center shrink-0"><Download size={22} /></span>
              <div className="flex-1">
                <p className="font-serif text-navy text-lg">{pdf.title || "Download PDF"}</p>
                {pdf.description && <p className="text-slate-500 text-sm">{pdf.description}</p>}
              </div>
              <span className="text-brass font-semibold text-sm hidden sm:block">{pdf.button_text || "Download PDF"}</span>
            </a>
          ))}

          {/* Tags + share */}
          <div className="flex flex-wrap items-center justify-between gap-4 mt-10 pt-6 border-t border-slate-200">
            <div className="flex flex-wrap gap-2">
              {post.tags?.map((t) => <span key={t} className="inline-flex items-center gap-1 text-xs bg-ivory-dark text-slate-600 px-3 py-1 rounded-full"><Tag size={11} />{t}</span>)}
            </div>
            <button onClick={share} className="inline-flex items-center gap-2 text-navy hover:text-brass text-sm font-medium" data-testid="article-share"><Share2 size={15} /> Share</button>
          </div>

          {/* Lead CTA */}
          <div className="bg-navy rounded-lg p-8 mt-12 text-center" data-testid="article-lead-cta">
            <h3 className="font-serif text-2xl text-ivory mb-3">Need legal assistance regarding a similar matter?</h3>
            <p className="text-ivory/60 mb-6">Submit an enquiry to discuss your legal matter.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-brass hover:bg-brass-hover text-navy font-semibold px-8 py-3.5 rounded-sm transition-colors">Send an Enquiry <ArrowRight size={17} /></Link>
          </div>
        </div>

        {/* Related */}
        {post.related?.length > 0 && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16" data-testid="article-related">
            <h2 className="font-serif text-2xl text-navy mb-6">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {post.related.map((r) => (
                <Link key={r.id} to={`/blog/${r.slug}`} className="group">
                  <div className="rounded-lg overflow-hidden mb-3 border border-slate-200" style={{ aspectRatio: "3/2" }}>
                    {r.featured_image ? <img src={resolveImg(r.featured_image)} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-navy/5" />}
                  </div>
                  <h3 className="font-serif text-base text-navy group-hover:text-brass transition-colors leading-snug">{r.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </PublicLayout>
  );
}
